import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../utils/axiosConfig';
import { useAuth } from '../context/AuthContext';
import '../styles/SuperAdminPage.css';

const MONTH_NAMES = [
  '', 'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
  'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر',
];

const ACQUISITION_LABELS = {
  offer_viewed: 'مشاهدة العرض',
  registration_selected: 'فتح التسجيل',
  form_started: 'بدء النموذج',
  signup_stored: 'طلبات محفوظة',
  offer_shared: 'مشاركة العرض',
};

function buildRecentMonthOptions() {
  const options = [];
  const now = new Date();
  for (let i = 0; i < 12; i += 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    options.push({ year: d.getFullYear(), month: d.getMonth() + 1 });
  }
  return options;
}

export default function SuperAdminPage() {
  const { switchToAccount } = useAuth();
  const { tab } = useParams();
  const activeTab = tab || 'accounts';
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [createForm, setCreateForm] = useState({ title: '', phone: '', description: '', address: '', whatsapp: '', category_id: '', is_free: false });
  const [editForm, setEditForm] = useState({ title: '', phone: '', description: '', address: '', whatsapp: '', category_id: '', is_free: false });
  const [submitting, setSubmitting] = useState(false);

  // Category management state
  const [showCreateCat, setShowCreateCat] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [catForm, setCatForm] = useState({ name: '', name_ar: '', icon: '' });
  const [editCatForm, setEditCatForm] = useState({ name: '', name_ar: '', icon: '' });

  const [blacklistUsers, setBlacklistUsers] = useState([]);
  const [blacklistLoading, setBlacklistLoading] = useState(false);
  const [unblockingUserId, setUnblockingUserId] = useState(null);

  // Business join requests state
  const [joinRequests, setJoinRequests] = useState([]);
  const [joinLoading, setJoinLoading] = useState(false);
  const [joinProcessingId, setJoinProcessingId] = useState(null);
  const [acquisitionSummary, setAcquisitionSummary] = useState(null);

  // Global users list state
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersTotalPages, setUsersTotalPages] = useState(1);
  const [userSearch, setUserSearch] = useState('');
  const [userSort, setUserSort] = useState('last_reservation');
  const [userOrder, setUserOrder] = useState('desc');
  const [userPage, setUserPage] = useState(1);
  const USERS_PER_PAGE = 20;

  const now = new Date();
  const [smsYear, setSmsYear] = useState(now.getFullYear());
  const [smsMonth, setSmsMonth] = useState(now.getMonth() + 1);
  const [smsUsage, setSmsUsage] = useState(null);
  const [smsLoading, setSmsLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteStep, setDeleteStep] = useState('warning');
  const [deleteCode, setDeleteCode] = useState('');
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [welcomeSmsSendingId, setWelcomeSmsSendingId] = useState(null);
  const [loginCodeLoadingId, setLoginCodeLoadingId] = useState(null);
  const [loginCodeInfo, setLoginCodeInfo] = useState(null);
  const [sendSmsSelectedIds, setSendSmsSelectedIds] = useState([]);
  const [sendSmsMessage, setSendSmsMessage] = useState('');
  const [sendSmsSending, setSendSmsSending] = useState(false);
  const [sendSmsResults, setSendSmsResults] = useState(null);
  const [sendSmsDropdownOpen, setSendSmsDropdownOpen] = useState(false);
  const [sendSmsFilter, setSendSmsFilter] = useState('');
  const [inboxMessages, setInboxMessages] = useState([]);
  const [inboxLoading, setInboxLoading] = useState(false);
  const [inboxUnreadCount, setInboxUnreadCount] = useState(0);
  const [inboxSelectedIds, setInboxSelectedIds] = useState([]);
  const [inboxUnreadOnly, setInboxUnreadOnly] = useState(false);
  const [inboxBusy, setInboxBusy] = useState(false);
  const monthOptions = buildRecentMonthOptions();

  const sendSmsEligibleAccounts = accounts.filter(
    (acc) => acc.phone && !acc.is_blocked,
  );
  const sendSmsFilteredAccounts = sendSmsEligibleAccounts.filter((acc) => {
    const q = sendSmsFilter.trim().toLowerCase();
    if (!q) return true;
    return (
      (acc.title || '').toLowerCase().includes(q)
      || (acc.phone || '').includes(q)
    );
  });

  const closeDeleteModal = () => {
    setDeleteTarget(null);
    setDeleteStep('warning');
    setDeleteCode('');
    setDeleteSubmitting(false);
  };

  const fetchAccounts = async () => {
    try {
      const { data } = await api.get('/backoffice/superadmin/accounts');
      if (data.success) setAccounts(data.accounts);
    } catch (err) {
      toast.error('خطأ في تحميل الحسابات');
    }
  };

  const fetchCategories = async () => {
    try {
      const { data } = await api.get('/backoffice/superadmin/categories');
      if (data.success) setCategories(data.categories);
    } catch (err) {
      toast.error('خطأ في تحميل الفئات');
    }
  };

  const fetchSmsUsage = async (year = smsYear, month = smsMonth) => {
    setSmsLoading(true);
    try {
      const { data } = await api.get('/backoffice/superadmin/sms-usage', {
        params: { year, month },
      });
      if (data.success) setSmsUsage(data);
      else toast.error(data.message || 'خطأ في تحميل إحصائيات SMS');
    } catch (err) {
      toast.error('خطأ في تحميل إحصائيات SMS');
    } finally {
      setSmsLoading(false);
    }
  };

  const fetchBlacklist = async () => {
    setBlacklistLoading(true);
    try {
      const { data } = await api.get('/backoffice/superadmin/booking-blacklist');
      if (data.success) setBlacklistUsers(data.users || []);
    } catch (err) {
      toast.error('خطأ في تحميل قائمة الحظر');
    } finally {
      setBlacklistLoading(false);
    }
  };

  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const { data } = await api.get('/backoffice/superadmin/users', {
        params: {
          search: userSearch.trim() || undefined,
          sort: userSort,
          order: userOrder,
          page: userPage,
          per_page: USERS_PER_PAGE,
        },
      });
      if (data.success) {
        setUsers(data.users || []);
        setUsersTotal(data.total || 0);
        setUsersTotalPages(data.total_pages || 1);
      } else {
        toast.error(data.message || 'خطأ في تحميل المستخدمين');
      }
    } catch (err) {
      toast.error('خطأ في تحميل المستخدمين');
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    Promise.all([fetchAccounts(), fetchCategories()]).finally(() => setLoading(false));
  }, []);

  const fetchJoinRequests = async () => {
    setJoinLoading(true);
    try {
      const { data } = await api.get('/backoffice/superadmin/join-requests');
      if (data.success) setJoinRequests(data.requests || []);
      else toast.error(data.message || 'خطأ في تحميل الطلبات');
    } catch (err) {
      toast.error('خطأ في تحميل طلبات الانضمام');
    } finally {
      setJoinLoading(false);
    }
  };

  const fetchAcquisitionSummary = async () => {
    try {
      const { data } = await api.get('/backoffice/superadmin/acquisition-summary');
      if (data.success) setAcquisitionSummary(data);
    } catch {
      setAcquisitionSummary(null);
    }
  };

  const handleAcceptJoinRequest = async (req) => {
    const reqId = req.id;
    const suggested = req.login_phone || req.phone || '';
    const phone = window.prompt(
      'رقم دخول مسؤول الحساب (بصيغة دولية):',
      suggested
    );
    if (phone === null) return;
    if (!phone.trim()) {
      toast.error('رقم الهاتف مطلوب');
      return;
    }

    setJoinProcessingId(reqId);
    try {
      const { data } = await api.post(
        `/backoffice/superadmin/join-requests/${reqId}/accept`,
        { phone: phone.trim() }
      );
      if (data.success) {
        toast.success(data.message || 'تم قبول الطلب وإنشاء الحساب');
        fetchJoinRequests();
        fetchAccounts();
      } else {
        toast.error(data.message || 'حدث خطأ');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'حدث خطأ');
    } finally {
      setJoinProcessingId(null);
    }
  };

  const handleRejectJoinRequest = async (reqId) => {
    setJoinProcessingId(reqId);
    try {
      const { data } = await api.post(`/backoffice/superadmin/join-requests/${reqId}/reject`);
      if (data.success) {
        toast.success(data.message || 'تم رفض الطلب');
        fetchJoinRequests();
      } else {
        toast.error(data.message || 'حدث خطأ');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'حدث خطأ');
    } finally {
      setJoinProcessingId(null);
    }
  };

  const fetchInbox = async (unreadOnly = inboxUnreadOnly) => {
    setInboxLoading(true);
    try {
      const { data } = await api.get('/backoffice/superadmin/incoming-sms', {
        params: { unread_only: unreadOnly ? 'true' : 'false', per_page: 100 },
      });
      if (data.success) {
        setInboxMessages(data.messages || []);
        setInboxUnreadCount(data.unread_count || 0);
        setInboxSelectedIds([]);
      }
    } catch (err) {
      toast.error('خطأ في تحميل صندوق الرسائل');
    } finally {
      setInboxLoading(false);
    }
  };

  const toggleInboxMessage = (id) => {
    setInboxSelectedIds((prev) => (
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    ));
  };

  const toggleInboxSelectAll = () => {
    if (inboxSelectedIds.length === inboxMessages.length) {
      setInboxSelectedIds([]);
    } else {
      setInboxSelectedIds(inboxMessages.map((m) => m.id));
    }
  };

  const handleInboxMarkRead = async (isRead = true) => {
    if (!inboxSelectedIds.length) return toast.error('اختر رسالة واحدةحدة على الأقل');
    setInboxBusy(true);
    try {
      const { data } = await api.post('/backoffice/superadmin/incoming-sms/mark-read', {
        ids: inboxSelectedIds,
        is_read: isRead,
      });
      if (data.success) {
        toast.success(data.message);
        fetchInbox();
      } else {
        toast.error(data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'حدث خطأ');
    } finally {
      setInboxBusy(false);
    }
  };

  const handleInboxDelete = async () => {
    if (!inboxSelectedIds.length) return toast.error('اختر رسالة واحدةحدة على الأقل');
    if (!window.confirm(`حذف ${inboxSelectedIds.length} رسالة؟`)) return;
    setInboxBusy(true);
    try {
      const { data } = await api.post('/backoffice/superadmin/incoming-sms/delete', {
        ids: inboxSelectedIds,
      });
      if (data.success) {
        toast.success(data.message);
        fetchInbox();
      } else {
        toast.error(data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'حدث خطأ');
    } finally {
      setInboxBusy(false);
    }
  };

  const handleInboxSync = async () => {
    setInboxBusy(true);
    try {
      const { data } = await api.post('/backoffice/superadmin/incoming-sms/sync', { limit: 100 });
      if (data.success) {
        toast.success(data.message);
        fetchInbox();
      } else {
        toast.error(data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'فشل المزامنة من Twilio');
    } finally {
      setInboxBusy(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'blacklist') fetchBlacklist();
    if (activeTab === 'sms') fetchSmsUsage();
    if (activeTab === 'inbox') fetchInbox(inboxUnreadOnly);
    if (activeTab === 'join-requests') {
      fetchJoinRequests();
      fetchAcquisitionSummary();
    }
  }, [activeTab, inboxUnreadOnly]);

  // Reset to first page when search/sort changes.
  useEffect(() => {
    setUserPage(1);
  }, [userSearch, userSort, userOrder]);

  // Fetch users (debounced) whenever the tab is active and query params change.
  useEffect(() => {
    if (activeTab !== 'users') return undefined;
    const t = setTimeout(fetchUsers, 300);
    return () => clearTimeout(t);
  }, [activeTab, userSearch, userSort, userOrder, userPage]);

  const handleSmsPeriodChange = (e) => {
    const value = e.target.value;
    if (!value) return;
    const [y, m] = value.split('-').map(Number);
    setSmsYear(y);
    setSmsMonth(m);
    fetchSmsUsage(y, m);
  };

  // ── Account handlers ────────────────────────────────────────────────

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!createForm.title.trim()) return toast.error('اسم الحساب مطلوب');
    if (!createForm.phone.trim()) return toast.error('رقم الهاتف مطلوب');
    setSubmitting(true);
    try {
      const payload = { ...createForm };
      if (!payload.category_id) delete payload.category_id;
      const { data } = await api.post('/backoffice/superadmin/accounts', payload);
      if (data.success) {
        toast.success(data.message);
        setShowCreate(false);
        setCreateForm({ title: '', phone: '', description: '', address: '', whatsapp: '', category_id: '', is_free: false });
        fetchAccounts();
      } else {
        toast.error(data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'حدث خطأ');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBlock = async (accountId) => {
    if (!window.confirm('هل أنت متأكد من حظر هذا الحساب؟ لن يتمكن العملاء أو المسؤول من الوصول إليه.')) return;
    try {
      const { data } = await api.put(`/backoffice/superadmin/accounts/${accountId}/block`);
      if (data.success) {
        toast.success(data.message);
        fetchAccounts();
      } else {
        toast.error(data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'حدث خطأ');
    }
  };

  const handleUnblock = async (accountId) => {
    try {
      const { data } = await api.put(`/backoffice/superadmin/accounts/${accountId}/unblock`);
      if (data.success) {
        toast.success(data.message);
        fetchAccounts();
      } else {
        toast.error(data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'حدث خطأ');
    }
  };

  const handleUnblockBookingUser = async (userId) => {
    if (!window.confirm('هل أنت متأكد من إلغاء حظر هذا المستخدم وإتاحة الحجز له؟')) return;
    setUnblockingUserId(userId);
    try {
      const { data } = await api.put(`/backoffice/superadmin/booking-blacklist/${userId}/unblock`);
      if (data.success) {
        toast.success(data.message);
        fetchBlacklist();
      } else {
        toast.error(data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'حدث خطأ');
    } finally {
      setUnblockingUserId(null);
    }
  };

  const openDelete = (account) => {
    setDeleteTarget(account);
    setDeleteStep('warning');
    setDeleteCode('');
  };

  const handleDeleteRequestCode = async () => {
    if (!deleteTarget) return;
    setDeleteSubmitting(true);
    try {
      const { data } = await api.post(`/backoffice/superadmin/accounts/${deleteTarget.id}/delete-request`);
      if (data.success) {
        toast.success(data.message);
        setDeleteStep('code');
      } else {
        toast.error(data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'حدث خطأ');
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const handleDeleteConfirm = async (e) => {
    e.preventDefault();
    if (!deleteTarget || !deleteCode.trim()) return toast.error('أدخل كود التأكيد');
    setDeleteSubmitting(true);
    try {
      const { data } = await api.post(
        `/backoffice/superadmin/accounts/${deleteTarget.id}/delete-confirm`,
        { code: deleteCode.trim() },
      );
      if (data.success) {
        toast.success(data.message);
        closeDeleteModal();
        fetchAccounts();
      } else {
        toast.error(data.message);
        if (data.invalid_code || data.code_expired || data.invalid_session) {
          closeDeleteModal();
        }
      }
    } catch (err) {
      const errData = err.response?.data;
      toast.error(errData?.message || 'حدث خطأ');
      if (errData?.invalid_code || errData?.code_expired || errData?.invalid_session) {
        closeDeleteModal();
      }
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const handleSendWelcomeSms = async (account) => {
    if (!account.phone) return toast.error('لا يوجد رقم هاتف لهذا الحساب');
    if (!window.confirm(`إرسال رسالة ترحيب إلى ${account.phone}؟`)) return;
    setWelcomeSmsSendingId(account.id);
    try {
      const { data } = await api.post(`/backoffice/superadmin/accounts/${account.id}/welcome-sms`);
      if (data.success) {
        toast.success(data.message);
      } else {
        toast.error(data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'حدث خطأ');
    } finally {
      setWelcomeSmsSendingId(null);
    }
  };

  const handleGetLoginCode = async (account) => {
    if (!account.phone) return toast.error('لا يوجد رقم هاتف لهذا الحساب');
    setLoginCodeLoadingId(account.id);
    try {
      const { data } = await api.post(`/backoffice/superadmin/accounts/${account.id}/login-code`);
      if (data.success) {
        setLoginCodeInfo({ ...data, title: account.title });
      } else {
        toast.error(data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'حدث خطأ');
    } finally {
      setLoginCodeLoadingId(null);
    }
  };

  const toggleSendSmsAccount = (accountId) => {
    setSendSmsSelectedIds((prev) => (
      prev.includes(accountId)
        ? prev.filter((id) => id !== accountId)
        : [...prev, accountId]
    ));
  };

  const selectAllSendSmsAccounts = () => {
    setSendSmsSelectedIds(sendSmsFilteredAccounts.map((acc) => acc.id));
  };

  const clearSendSmsSelection = () => {
    setSendSmsSelectedIds([]);
  };

  const handleSendSms = async () => {
    if (!sendSmsSelectedIds.length) return toast.error('اختر حساباً واحداً على الأقل');
    if (!sendSmsMessage.trim()) return toast.error('أدخل نص الرسالة');
    if (!window.confirm(`إرسال الرسالة إلى ${sendSmsSelectedIds.length} صالون؟`)) return;

    setSendSmsSending(true);
    setSendSmsResults(null);
    try {
      const { data } = await api.post('/backoffice/superadmin/send-sms', {
        account_ids: sendSmsSelectedIds,
        message: sendSmsMessage,
      });
      if (data.success) {
        toast.success(data.message);
        setSendSmsResults(data);
      } else {
        toast.error(data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'حدث خطأ');
    } finally {
      setSendSmsSending(false);
    }
  };

  const handleSwitch = async (accountId) => {
    try {
      const { data } = await api.post(`/backoffice/superadmin/accounts/${accountId}/switch`);
      if (data.success) {
        switchToAccount(data.access_token, data.account);
        toast.success(data.message);
        window.location.href = '/';
      } else {
        toast.error(data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'حدث خطأ');
    }
  };

  const openEdit = (account) => {
    setEditingAccount(account);
    setEditForm({
      title: account.title || '',
      phone: account.phone || '',
      description: account.description || '',
      address: account.address || '',
      whatsapp: account.whatsapp || '',
      category_id: account.category_id || '',
      is_free: !!account.is_free,
    });
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!editingAccount) return;
    setSubmitting(true);
    try {
      const payload = { ...editForm };
      if (!payload.category_id) payload.category_id = null;
      const { data } = await api.put(`/backoffice/superadmin/accounts/${editingAccount.id}`, payload);
      if (data.success) {
        toast.success(data.message);
        setEditingAccount(null);
        fetchAccounts();
      } else {
        toast.error(data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'حدث خطأ');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Category handlers ───────────────────────────────────────────────

  const handleCreateCat = async (e) => {
    e.preventDefault();
    if (!catForm.name.trim() || !catForm.name_ar.trim()) return toast.error('اسم الفئة بالعربية والإنجليزية مطلوب');
    setSubmitting(true);
    try {
      const { data } = await api.post('/backoffice/superadmin/categories', catForm);
      if (data.success) {
        toast.success(data.message);
        setShowCreateCat(false);
        setCatForm({ name: '', name_ar: '', icon: '' });
        fetchCategories();
      } else {
        toast.error(data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'حدث خطأ');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditCat = (cat) => {
    setEditingCategory(cat);
    setEditCatForm({ name: cat.name || '', name_ar: cat.name_ar || '', icon: cat.icon || '' });
  };

  const handleEditCat = async (e) => {
    e.preventDefault();
    if (!editingCategory) return;
    setSubmitting(true);
    try {
      const { data } = await api.put(`/backoffice/superadmin/categories/${editingCategory.id}`, editCatForm);
      if (data.success) {
        toast.success(data.message);
        setEditingCategory(null);
        fetchCategories();
      } else {
        toast.error(data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'حدث خطأ');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCat = async (catId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذه الفئة؟')) return;
    try {
      const { data } = await api.delete(`/backoffice/superadmin/categories/${catId}`);
      if (data.success) {
        toast.success(data.message);
        fetchCategories();
      } else {
        toast.error(data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'حدث خطأ');
    }
  };

  // ── Category dropdown helper ────────────────────────────────────────

  const CategorySelect = ({ value, onChange }) => (
    <div className="form-group">
      <label>الفئة</label>
      <select value={value} onChange={onChange} className="sa-select">
        <option value="">-- بدون فئة --</option>
        {categories.map((cat) => (
          <option key={cat.id} value={cat.id}>{cat.name_ar} ({cat.name})</option>
        ))}
      </select>
    </div>
  );

  const handleUserSort = (column) => {
    if (userSort === column) {
      setUserOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setUserSort(column);
      setUserOrder('desc');
    }
  };

  const sortIndicator = (column) => {
    if (userSort !== column) return '';
    return userOrder === 'asc' ? ' ▲' : ' ▼';
  };

  const formatUserDate = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString('ar-EG', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (loading) return <div className="page-loading">جاري التحميل...</div>;

  return (
    <div className="superadmin-page">
      {/* ── Accounts Tab ──────────────────────────────────────────────── */}
      {activeTab === 'accounts' && (
        <>
          <div className="sa-header">
            <div>
              <h1>إدارة الحسابات</h1>
              <p className="sa-header-hint">صفحة المدير العام فقط — استخدم «SMS ترحيب» لإرسال رسالة ترحيب لمسؤول كل حساب</p>
            </div>
            <button className="sa-create-btn" onClick={() => setShowCreate(true)}>
              + إضافة حساب جديد
            </button>
          </div>

          <div className="sa-stats">
            <div className="sa-stat-card">
              <span className="sa-stat-number">{accounts.length}</span>
              <span className="sa-stat-label">إجمالي الحسابات</span>
            </div>
            <div className="sa-stat-card">
              <span className="sa-stat-number">{accounts.filter(a => !a.is_blocked).length}</span>
              <span className="sa-stat-label">حسابات نشطة</span>
            </div>
            <div className="sa-stat-card sa-stat-danger">
              <span className="sa-stat-number">{accounts.filter(a => a.is_blocked).length}</span>
              <span className="sa-stat-label">حسابات محظورة</span>
            </div>
          </div>

          <div className="sa-accounts-list">
            {accounts.map((acc) => (
              <div key={acc.id} className={`sa-account-card ${acc.is_blocked ? 'sa-blocked' : ''}`}>
                <div className="sa-account-info">
                  <h3>{acc.title}</h3>
                  <p className="sa-account-phone" dir="ltr">{acc.phone || 'بدون رقم'}</p>
                  {acc.category_name_ar && <span className="sa-badge-category">{acc.category_name_ar}</span>}
                  {acc.is_blocked && <span className="sa-badge-blocked">محظور</span>}
                  {!acc.is_blocked && <span className="sa-badge-active">نشط</span>}
                  {acc.is_free && <span className="sa-badge-free">مجاني</span>}
                </div>
                <div className="sa-account-actions">
                  <button
                    className="sa-btn sa-btn-welcome"
                    onClick={() => handleSendWelcomeSms(acc)}
                    disabled={!acc.phone || welcomeSmsSendingId === acc.id}
                    title="إرسال رسالة ترحيب لمسؤول الحساب"
                  >
                    {welcomeSmsSendingId === acc.id ? 'جاري الإرسال...' : 'SMS ترحيب'}
                  </button>
                  <button
                    className="sa-btn sa-btn-login-code"
                    onClick={() => handleGetLoginCode(acc)}
                    disabled={!acc.phone || loginCodeLoadingId === acc.id}
                    title="إصدار كود دخول لإرساله يدوياً عبر واتساب"
                  >
                    {loginCodeLoadingId === acc.id ? '...' : 'كود دخول'}
                  </button>
                  <button className="sa-btn sa-btn-switch" onClick={() => handleSwitch(acc.id)} title="الدخول لهذا الحساب">
                    &#8644; تبديل
                  </button>
                  <button className="sa-btn sa-btn-edit" onClick={() => openEdit(acc)} title="تعديل">
                    &#9998; تعديل
                  </button>
                  {acc.is_blocked ? (
                    <button className="sa-btn sa-btn-unblock" onClick={() => handleUnblock(acc.id)}>
                      &#10003; إلغاء الحظر
                    </button>
                  ) : (
                    <button className="sa-btn sa-btn-block" onClick={() => handleBlock(acc.id)}>
                      &#10007; حظر
                    </button>
                  )}
                  <button className="sa-btn sa-btn-delete" onClick={() => openDelete(acc)} title="حذف نهائي">
                    &#128465; حذف
                  </button>
                </div>
              </div>
            ))}
            {accounts.length === 0 && <p className="sa-empty">لا توجد حسابات</p>}
          </div>

          {/* Create Account Modal */}
          {showCreate && (
            <div className="sa-modal-overlay" onClick={() => setShowCreate(false)}>
              <div className="sa-modal" onClick={(e) => e.stopPropagation()}>
                <h2>إضافة حساب جديد</h2>
                <form onSubmit={handleCreate}>
                  <div className="form-group">
                    <label>اسم الصالون *</label>
                    <input
                      value={createForm.title}
                      onChange={(e) => setCreateForm(f => ({ ...f, title: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>رقم الهاتف (للدخول) *</label>
                    <input
                      value={createForm.phone}
                      onChange={(e) => setCreateForm(f => ({ ...f, phone: e.target.value }))}
                      dir="ltr"
                      required
                    />
                  </div>
                  <CategorySelect
                    value={createForm.category_id}
                    onChange={(e) => setCreateForm(f => ({ ...f, category_id: e.target.value }))}
                  />
                  <label className={`sa-free-toggle ${createForm.is_free ? 'is-on' : ''}`}>
                    <span className="sa-free-toggle-text">
                      <span className="sa-free-toggle-title">&#11088; حساب مجاني</span>
                      <span className="sa-free-toggle-hint">
                        {createForm.is_free ? 'ضمن عرض الـ 20 صالون المجاني' : 'حساب عادي (ليس ضمن الـ 20 المجانية)'}
                      </span>
                    </span>
                    <span className="sa-switch">
                      <input
                        type="checkbox"
                        checked={createForm.is_free}
                        onChange={(e) => setCreateForm(f => ({ ...f, is_free: e.target.checked }))}
                      />
                      <span className="sa-switch-slider" />
                    </span>
                  </label>
                  <div className="form-group">
                    <label>الوصف</label>
                    <textarea
                      value={createForm.description}
                      onChange={(e) => setCreateForm(f => ({ ...f, description: e.target.value }))}
                      rows={2}
                    />
                  </div>
                  <div className="form-group">
                    <label>العنوان</label>
                    <input
                      value={createForm.address}
                      onChange={(e) => setCreateForm(f => ({ ...f, address: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label>واتساب</label>
                    <input
                      value={createForm.whatsapp}
                      onChange={(e) => setCreateForm(f => ({ ...f, whatsapp: e.target.value }))}
                      dir="ltr"
                    />
                  </div>
                  <div className="sa-modal-actions">
                    <button type="submit" className="sa-btn sa-btn-primary" disabled={submitting}>
                      {submitting ? 'جاري الإنشاء...' : 'إنشاء الحساب'}
                    </button>
                    <button type="button" className="sa-btn sa-btn-cancel" onClick={() => setShowCreate(false)}>
                      إلغاء
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Manual Login Code Modal */}
          {loginCodeInfo && (
            <div className="sa-modal-overlay" onClick={() => setLoginCodeInfo(null)}>
              <div className="sa-modal sa-login-code-modal" onClick={(e) => e.stopPropagation()}>
                <h2>كود دخول لـ {loginCodeInfo.title}</h2>
                <p className="sa-login-code-phone" dir="ltr">{loginCodeInfo.phone}</p>
                <div className="sa-login-code-value" dir="ltr">{loginCodeInfo.code}</div>
                <p className="sa-login-code-note">
                  صالح لمدة {loginCodeInfo.expires_in_minutes} دقيقة. أرسله لصاحب الحساب
                  عبر واتساب ليتمكن من الدخول بنفسه.
                </p>
                <div className="sa-modal-actions">
                  <a
                    className="sa-btn sa-btn-primary"
                    href={loginCodeInfo.whatsapp_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    إرسال عبر واتساب
                  </a>
                  <button
                    type="button"
                    className="sa-btn"
                    onClick={() => {
                      navigator.clipboard?.writeText(loginCodeInfo.code);
                      toast.success('تم نسخ الكود');
                    }}
                  >
                    نسخ الكود
                  </button>
                  <button type="button" className="sa-btn sa-btn-cancel" onClick={() => setLoginCodeInfo(null)}>
                    إغلاق
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Delete Account Modal */}
          {deleteTarget && (
            <div className="sa-modal-overlay" onClick={closeDeleteModal}>
              <div className="sa-modal sa-delete-modal" onClick={(e) => e.stopPropagation()}>
                {deleteStep === 'warning' ? (
                  <>
                    <h2>حذف الحساب نهائياً</h2>
                    <p className="sa-delete-warning">
                      سيتم حذف حساب <strong>{deleteTarget.title}</strong> وجميع بياناته بشكل نهائي،
                      بما في ذلك الإعدادات والعملاء والحجوزات والصور. لا يمكن التراجع عن هذا الإجراء.
                    </p>
                    <p className="sa-delete-info">
                      سيتم إرسال كود تأكيد عبر SMS إلى هاتفك كمدير عام.
                    </p>
                    <div className="sa-modal-actions">
                      <button
                        type="button"
                        className="sa-btn sa-btn-delete-confirm"
                        onClick={handleDeleteRequestCode}
                        disabled={deleteSubmitting}
                      >
                        {deleteSubmitting ? 'جاري الإرسال...' : 'إرسال كود التأكيد'}
                      </button>
                      <button type="button" className="sa-btn sa-btn-cancel" onClick={closeDeleteModal}>
                        إلغاء
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <h2>تأكيد الحذف</h2>
                    <p className="sa-delete-info">
                      تم إرسال كود التأكيد إلى هاتفك. أدخل الكود لحذف حساب <strong>{deleteTarget.title}</strong>.
                    </p>
                    <form onSubmit={handleDeleteConfirm}>
                      <div className="form-group">
                        <label>كود التأكيد</label>
                        <input
                          value={deleteCode}
                          onChange={(e) => setDeleteCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          placeholder="000000"
                          dir="ltr"
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          required
                          maxLength={6}
                        />
                      </div>
                      <div className="sa-modal-actions">
                        <button type="submit" className="sa-btn sa-btn-delete-confirm" disabled={deleteSubmitting}>
                          {deleteSubmitting ? 'جاري الحذف...' : 'تأكيد الحذف'}
                        </button>
                        <button type="button" className="sa-btn sa-btn-cancel" onClick={closeDeleteModal}>
                          إلغاء
                        </button>
                      </div>
                    </form>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Edit Account Modal */}
          {editingAccount && (
            <div className="sa-modal-overlay" onClick={() => setEditingAccount(null)}>
              <div className="sa-modal" onClick={(e) => e.stopPropagation()}>
                <h2>تعديل الحساب</h2>
                <form onSubmit={handleEdit}>
                  <div className="form-group">
                    <label>اسم الصالون</label>
                    <input
                      value={editForm.title}
                      onChange={(e) => setEditForm(f => ({ ...f, title: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>رقم الهاتف</label>
                    <input
                      value={editForm.phone}
                      onChange={(e) => setEditForm(f => ({ ...f, phone: e.target.value }))}
                      dir="ltr"
                    />
                  </div>
                  <CategorySelect
                    value={editForm.category_id}
                    onChange={(e) => setEditForm(f => ({ ...f, category_id: e.target.value }))}
                  />
                  <label className={`sa-free-toggle ${editForm.is_free ? 'is-on' : ''}`}>
                    <span className="sa-free-toggle-text">
                      <span className="sa-free-toggle-title">&#11088; حساب مجاني</span>
                      <span className="sa-free-toggle-hint">
                        {editForm.is_free ? 'ضمن عرض الـ 20 صالون المجاني' : 'حساب عادي (ليس ضمن الـ 20 المجانية)'}
                      </span>
                    </span>
                    <span className="sa-switch">
                      <input
                        type="checkbox"
                        checked={editForm.is_free}
                        onChange={(e) => setEditForm(f => ({ ...f, is_free: e.target.checked }))}
                      />
                      <span className="sa-switch-slider" />
                    </span>
                  </label>
                  <div className="form-group">
                    <label>الوصف</label>
                    <textarea
                      value={editForm.description}
                      onChange={(e) => setEditForm(f => ({ ...f, description: e.target.value }))}
                      rows={2}
                    />
                  </div>
                  <div className="form-group">
                    <label>العنوان</label>
                    <input
                      value={editForm.address}
                      onChange={(e) => setEditForm(f => ({ ...f, address: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label>واتساب</label>
                    <input
                      value={editForm.whatsapp}
                      onChange={(e) => setEditForm(f => ({ ...f, whatsapp: e.target.value }))}
                      dir="ltr"
                    />
                  </div>
                  <div className="sa-welcome-sms-box">
                    <p>إرسال رسالة ترحيب إلى مسؤول الحساب عبر SMS</p>
                    <p className="sa-welcome-sms-phone" dir="ltr">{editForm.phone || 'لا يوجد رقم هاتف'}</p>
                    <button
                      type="button"
                      className="sa-btn sa-btn-welcome sa-btn-welcome-modal"
                      onClick={() => handleSendWelcomeSms(editingAccount)}
                      disabled={!editForm.phone || welcomeSmsSendingId === editingAccount.id}
                    >
                      {welcomeSmsSendingId === editingAccount.id ? 'جاري الإرسال...' : 'إرسال SMS ترحيب'}
                    </button>
                  </div>
                  <div className="sa-modal-actions">
                    <button type="submit" className="sa-btn sa-btn-primary" disabled={submitting}>
                      {submitting ? 'جاري الحفظ...' : 'حفظ التغييرات'}
                    </button>
                    <button type="button" className="sa-btn sa-btn-cancel" onClick={() => setEditingAccount(null)}>
                      إلغاء
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Categories Tab ────────────────────────────────────────────── */}
      {activeTab === 'categories' && (
        <>
          <div className="sa-header">
            <h1>إدارة الفئات</h1>
            <button className="sa-create-btn" onClick={() => setShowCreateCat(true)}>
              + إضافة فئة جديدة
            </button>
          </div>

          <div className="sa-categories-list">
            {categories.map((cat) => (
              <div key={cat.id} className="sa-category-card">
                <div className="sa-category-info">
                  <h3>{cat.name_ar}</h3>
                  <p className="sa-category-name-en">{cat.name}</p>
                  {cat.icon && <span className="sa-category-icon">{cat.icon}</span>}
                  <span className="sa-category-count">{cat.account_count || 0} حساب</span>
                </div>
                <div className="sa-account-actions">
                  <button className="sa-btn sa-btn-edit" onClick={() => openEditCat(cat)}>
                    &#9998; تعديل
                  </button>
                  <button className="sa-btn sa-btn-block" onClick={() => handleDeleteCat(cat.id)}>
                    &#10007; حذف
                  </button>
                </div>
              </div>
            ))}
            {categories.length === 0 && <p className="sa-empty">لا توجد فئات</p>}
          </div>

          {/* Create Category Modal */}
          {showCreateCat && (
            <div className="sa-modal-overlay" onClick={() => setShowCreateCat(false)}>
              <div className="sa-modal" onClick={(e) => e.stopPropagation()}>
                <h2>إضافة فئة جديدة</h2>
                <form onSubmit={handleCreateCat}>
                  <div className="form-group">
                    <label>اسم الفئة (عربي) *</label>
                    <input
                      value={catForm.name_ar}
                      onChange={(e) => setCatForm(f => ({ ...f, name_ar: e.target.value }))}
                      placeholder="مثال: حلاقة"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>اسم الفئة (إنجليزي) *</label>
                    <input
                      value={catForm.name}
                      onChange={(e) => setCatForm(f => ({ ...f, name: e.target.value }))}
                      placeholder="e.g. barbershop"
                      dir="ltr"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>أيقونة</label>
                    <input
                      value={catForm.icon}
                      onChange={(e) => setCatForm(f => ({ ...f, icon: e.target.value }))}
                      placeholder="e.g. scissors, nail-polish"
                      dir="ltr"
                    />
                  </div>
                  <div className="sa-modal-actions">
                    <button type="submit" className="sa-btn sa-btn-primary" disabled={submitting}>
                      {submitting ? 'جاري الإنشاء...' : 'إنشاء الفئة'}
                    </button>
                    <button type="button" className="sa-btn sa-btn-cancel" onClick={() => setShowCreateCat(false)}>
                      إلغاء
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Edit Category Modal */}
          {editingCategory && (
            <div className="sa-modal-overlay" onClick={() => setEditingCategory(null)}>
              <div className="sa-modal" onClick={(e) => e.stopPropagation()}>
                <h2>تعديل الفئة</h2>
                <form onSubmit={handleEditCat}>
                  <div className="form-group">
                    <label>اسم الفئة (عربي)</label>
                    <input
                      value={editCatForm.name_ar}
                      onChange={(e) => setEditCatForm(f => ({ ...f, name_ar: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>اسم الفئة (إنجليزي)</label>
                    <input
                      value={editCatForm.name}
                      onChange={(e) => setEditCatForm(f => ({ ...f, name: e.target.value }))}
                      dir="ltr"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>أيقونة</label>
                    <input
                      value={editCatForm.icon}
                      onChange={(e) => setEditCatForm(f => ({ ...f, icon: e.target.value }))}
                      dir="ltr"
                    />
                  </div>
                  <div className="sa-modal-actions">
                    <button type="submit" className="sa-btn sa-btn-primary" disabled={submitting}>
                      {submitting ? 'جاري الحفظ...' : 'حفظ التغييرات'}
                    </button>
                    <button type="button" className="sa-btn sa-btn-cancel" onClick={() => setEditingCategory(null)}>
                      إلغاء
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </>
      )}

      {activeTab === 'send-sms' && (
        <>
          <div className="sa-header">
            <div>
              <h1>إرسال SMS لأصحاب الصالونات</h1>
              <p className="sa-header-hint">
                اختر حساباً واحداً أو أكثر وأرسل رسالة مخصصة. المتغيرات: {'{salon}'} {'{name}'}
              </p>
            </div>
          </div>

          <div className="sa-send-sms-panel">
            <div className="sa-send-sms-field">
              <label>الصالونات</label>
              <div className="sa-ms-dropdown">
                <button
                  type="button"
                  className="sa-ms-trigger"
                  onClick={() => setSendSmsDropdownOpen((open) => !open)}
                >
                  {sendSmsSelectedIds.length
                    ? `${sendSmsSelectedIds.length} صالون محدد`
                    : 'اختر الصالونات...'}
                  <span>{sendSmsDropdownOpen ? '▴' : '▾'}</span>
                </button>
                {sendSmsDropdownOpen && (
                  <div className="sa-ms-menu">
                    <input
                      type="text"
                      className="sa-ms-search"
                      placeholder="بحث بالاسم أو الهاتف..."
                      value={sendSmsFilter}
                      onChange={(e) => setSendSmsFilter(e.target.value)}
                      autoFocus
                    />
                    <div className="sa-ms-actions">
                      <button type="button" onClick={selectAllSendSmsAccounts}>تحديد الظاهر</button>
                      <button type="button" onClick={clearSendSmsSelection}>مسح الكل</button>
                    </div>
                    <div className="sa-ms-list">
                      {sendSmsFilteredAccounts.length === 0 ? (
                        <p className="sa-ms-empty">لا توجد حسابات مطابقة</p>
                      ) : (
                        sendSmsFilteredAccounts.map((acc) => (
                          <label key={acc.id} className="sa-ms-option">
                            <input
                              type="checkbox"
                              checked={sendSmsSelectedIds.includes(acc.id)}
                              onChange={() => toggleSendSmsAccount(acc.id)}
                            />
                            <span className="sa-ms-option-title">{acc.title}</span>
                            <span className="sa-ms-option-phone" dir="ltr">{acc.phone}</span>
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
              {sendSmsSelectedIds.length > 0 && (
                <div className="sa-ms-chips">
                  {accounts
                    .filter((acc) => sendSmsSelectedIds.includes(acc.id))
                    .map((acc) => (
                      <button
                        key={acc.id}
                        type="button"
                        className="sa-ms-chip"
                        onClick={() => toggleSendSmsAccount(acc.id)}
                        title="إزالة"
                      >
                        {acc.title} ×
                      </button>
                    ))}
                </div>
              )}
            </div>

            <div className="sa-send-sms-field">
              <label htmlFor="sa-send-sms-body">نص الرسالة</label>
              <textarea
                id="sa-send-sms-body"
                rows={5}
                value={sendSmsMessage}
                onChange={(e) => setSendSmsMessage(e.target.value)}
                placeholder="مرحباً {salon}، ..."
              />
              <p className="sa-send-sms-hint">
                {'{salon}'} و {'{name}'} يُستبدلان باسم الصالون لكل مستلم.
              </p>
            </div>

            <button
              type="button"
              className="sa-btn sa-btn-primary sa-send-sms-submit"
              onClick={handleSendSms}
              disabled={sendSmsSending || !sendSmsSelectedIds.length || !sendSmsMessage.trim()}
            >
              {sendSmsSending
                ? 'جاري الإرسال...'
                : `إرسال إلى ${sendSmsSelectedIds.length || 0} صالون`}
            </button>
          </div>

          {sendSmsResults && (
            <div className="sa-send-sms-results">
              <h2>نتائج الإرسال</h2>
              <p>
                نجح: {sendSmsResults.sent || 0} · فشل: {sendSmsResults.failed || 0} · تخطي:{' '}
                {sendSmsResults.skipped || 0}
              </p>
              <div className="sa-sms-table-wrap">
                <table className="sa-sms-table">
                  <thead>
                    <tr>
                      <th>الصالون</th>
                      <th>الهاتف</th>
                      <th>الحالة</th>
                      <th>تفاصيل</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(sendSmsResults.results || []).map((row) => (
                      <tr key={row.account_id}>
                        <td>{row.title || '—'}</td>
                        <td dir="ltr">{row.phone || '—'}</td>
                        <td>
                          {row.status === 'sent' && <span className="sa-badge-ok">أُرسلت</span>}
                          {row.status === 'failed' && <span className="sa-badge-blocked">فشل</span>}
                          {row.status === 'skipped' && <span className="sa-badge-muted">تخطي</span>}
                        </td>
                        <td>{row.error || row.message_sid || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {activeTab === 'sms' && (
        <>
          <div className="sa-header sa-sms-header">
            <h1>إحصائيات رسائل SMS</h1>
            <div className="sa-sms-filters">
              <label htmlFor="sa-sms-period">الشهر</label>
              <select
                id="sa-sms-period"
                value={`${smsYear}-${smsMonth}`}
                onChange={handleSmsPeriodChange}
                disabled={smsLoading}
              >
                {monthOptions.map((opt) => (
                  <option key={`${opt.year}-${opt.month}`} value={`${opt.year}-${opt.month}`}>
                    {MONTH_NAMES[opt.month]} {opt.year}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <p className="sa-blacklist-intro">
            عدد الرسائل المرسلة عبر Twilio لكل صالون (تأكيد حجز، تذكير، إلغاء، رسائل جماعية من لوحة التحكم).
            يُحفظ سجل آخر 12 شهراً.
          </p>

          <div className="sa-stats">
            <div className="sa-stat-card">
              <span className="sa-stat-number">{smsUsage?.total_sms ?? '—'}</span>
              <span className="sa-stat-label">
                إجمالي الشهر ({MONTH_NAMES[smsMonth]} {smsYear})
              </span>
            </div>
            <div className="sa-stat-card">
              <span className="sa-stat-number">{smsUsage?.accounts?.length ?? '—'}</span>
              <span className="sa-stat-label">عدد الحسابات</span>
            </div>
          </div>

          {smsLoading ? (
            <p className="sa-empty">جاري التحميل...</p>
          ) : (
            <div className="sa-sms-table-wrap">
              <table className="sa-sms-table">
                <thead>
                  <tr>
                    <th>الصالون</th>
                    <th>هاتف المسؤول</th>
                    <th>رسائل الشهر</th>
                    <th>آخر 12 شهراً</th>
                  </tr>
                </thead>
                <tbody>
                  {(smsUsage?.accounts || [])
                    .slice()
                    .sort((a, b) => (b.sms_count || 0) - (a.sms_count || 0))
                    .map((acc) => (
                      <tr key={acc.id} className={acc.is_blocked ? 'sa-sms-row-blocked' : ''}>
                        <td>
                          {acc.title}
                          {acc.is_blocked && <span className="sa-badge-blocked">محظور</span>}
                        </td>
                        <td dir="ltr">{acc.phone || '—'}</td>
                        <td className="sa-sms-count">{acc.sms_count ?? 0}</td>
                        <td className="sa-sms-history">
                          {(acc.monthly_history || []).length === 0 ? (
                            <span className="sa-sms-muted">—</span>
                          ) : (
                            acc.monthly_history.map((h) => (
                              <span key={`${h.year}-${h.month}`} className="sa-sms-history-chip">
                                {MONTH_NAMES[h.month]?.slice(0, 3)} {String(h.year).slice(2)}: {h.sms_count}
                              </span>
                            ))
                          )}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
              {(!smsUsage?.accounts || smsUsage.accounts.length === 0) && (
                <p className="sa-empty">لا توجد بيانات لهذا الشهر</p>
              )}
            </div>
          )}
        </>
      )}

      {/* ── Users Tab ─────────────────────────────────────────────────── */}
      {activeTab === 'users' && (
        <>
          <div className="sa-header">
            <div>
              <h1>المستخدمون</h1>
              <p className="sa-header-hint">قائمة جميع المستخدمين مع عدد الحجوزات وتاريخ آخر حجز (عبر كل الصالونات)</p>
            </div>
          </div>

          <div className="sa-users-toolbar">
            <input
              type="text"
              className="sa-users-search"
              placeholder="بحث بالاسم أو رقم الهاتف..."
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
            />
            <div className="sa-users-total">
              {usersLoading ? 'جاري التحميل...' : `${usersTotal} مستخدم`}
            </div>
          </div>

          <div className="sa-sms-table-wrap">
            <table className="sa-sms-table sa-users-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th className="sa-sortable" onClick={() => handleUserSort('name')}>
                    الاسم{sortIndicator('name')}
                  </th>
                  <th>رقم الهاتف</th>
                  <th className="sa-sortable" onClick={() => handleUserSort('reservations')}>
                    عدد الحجوزات{sortIndicator('reservations')}
                  </th>
                  <th className="sa-sortable" onClick={() => handleUserSort('last_reservation')}>
                    آخر حجز{sortIndicator('last_reservation')}
                  </th>
                  <th className="sa-sortable" onClick={() => handleUserSort('created_at')}>
                    تاريخ التسجيل{sortIndicator('created_at')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {users.map((u, i) => (
                  <tr key={u.id}>
                    <td>{(userPage - 1) * USERS_PER_PAGE + i + 1}</td>
                    <td>{u.full_name || '—'}</td>
                    <td dir="ltr">{u.phone || '—'}</td>
                    <td className="sa-sms-count">{u.reservation_count}</td>
                    <td>{formatUserDate(u.last_reservation)}</td>
                    <td>{formatUserDate(u.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!usersLoading && users.length === 0 && (
              <p className="sa-empty">{userSearch ? 'لا توجد نتائج للبحث' : 'لا يوجد مستخدمون'}</p>
            )}
          </div>

          {usersTotalPages > 1 && (
            <div className="sa-pagination">
              <button
                className="sa-page-btn"
                onClick={() => setUserPage((p) => Math.max(1, p - 1))}
                disabled={userPage <= 1 || usersLoading}
              >
                السابق
              </button>
              <span className="sa-page-info">صفحة {userPage} من {usersTotalPages}</span>
              <button
                className="sa-page-btn"
                onClick={() => setUserPage((p) => Math.min(usersTotalPages, p + 1))}
                disabled={userPage >= usersTotalPages || usersLoading}
              >
                التالي
              </button>
            </div>
          )}
        </>
      )}

      {activeTab === 'join-requests' && (
        <>
          <div className="sa-header">
            <div>
              <h1>طلبات الانضمام</h1>
              <p className="sa-header-hint">
                عند القبول يُنشأ حساب مجاني ما دام أحد الأماكن العشرين متاحاً
              </p>
            </div>
          </div>

          {acquisitionSummary && (
            <div className="sa-stats">
              {Object.entries(ACQUISITION_LABELS).map(([event, label]) => (
                <div className="sa-stat-card" key={event}>
                  <span className="sa-stat-number">
                    {acquisitionSummary.totals?.[event] || 0}
                  </span>
                  <span className="sa-stat-label">{label}</span>
                </div>
              ))}
            </div>
          )}

          {joinLoading ? (
            <p className="sa-empty">جاري التحميل...</p>
          ) : (
            <div className="sa-accounts-list">
              {joinRequests.map((req) => (
                <div key={req.id} className={`sa-account-card ${req.status === 'rejected' ? 'sa-blocked' : ''}`}>
                  <div className="sa-account-info">
                    <h3>{req.business_name || req.contact_name || 'طلب انضمام'}</h3>
                    <p className="sa-account-phone" dir="ltr">{req.phone || '—'}</p>
                    {req.status === 'pending' && req.login_phone && req.login_phone !== req.phone && (
                      <p className="sa-account-phone" dir="ltr">رقم الدخول: {req.login_phone}</p>
                    )}
                    <div className="sa-join-meta">
                      {req.contact_name && <span className="sa-badge-category">الاسم: {req.contact_name}</span>}
                      {req.category && <span className="sa-badge-category">{req.category}</span>}
                      {(req.country || req.city) && (
                        <span className="sa-badge-category">
                          {[req.country, req.city].filter(Boolean).join(' - ')}
                        </span>
                      )}
                    </div>
                    {(req.utm_source || req.utm_medium || req.utm_campaign) && (
                      <p className="sa-acquisition-source">
                        المصدر: {[req.utm_source, req.utm_medium, req.utm_campaign]
                          .filter(Boolean)
                          .join(' / ')}
                      </p>
                    )}
                    {req.status === 'pending' && <span className="sa-badge-active">قيد الانتظار</span>}
                    {req.status === 'accepted' && <span className="sa-badge-active">تم القبول ✓</span>}
                    {req.status === 'rejected' && <span className="sa-badge-blocked">مرفوض</span>}
                    {req.created_at && (
                      <p className="sa-blacklist-date">
                        {new Date(req.created_at).toLocaleString('ar-EG')}
                      </p>
                    )}
                  </div>
                  <div className="sa-account-actions">
                    {req.status === 'pending' && (
                      <>
                        <button
                          className="sa-btn sa-btn-unblock"
                          onClick={() => handleAcceptJoinRequest(req)}
                          disabled={joinProcessingId === req.id}
                        >
                          {joinProcessingId === req.id ? '...' : '✓ قبول وإنشاء حساب'}
                        </button>
                        <button
                          className="sa-btn sa-btn-cancel"
                          onClick={() => handleRejectJoinRequest(req.id)}
                          disabled={joinProcessingId === req.id}
                        >
                          رفض
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
              {joinRequests.length === 0 && (
                <p className="sa-empty">لا توجد طلبات انضمام حالياً</p>
              )}
            </div>
          )}
        </>
      )}

      {activeTab === 'inbox' && (
        <>
          <div className="sa-header">
            <div>
              <h1>صندوق رسائل SMS الواردة</h1>
              <p className="sa-header-hint">
                الرسائل الواردة إلى رقم Twilio. يمكنك المزامنة من سجلات Twilio أو استقبالها مباشرة عبر الـ Webhook.
                غير مقروء: {inboxUnreadCount}
              </p>
            </div>
          </div>

          <div className="sa-inbox-toolbar">
            <label className="sa-inbox-filter">
              <input
                type="checkbox"
                checked={inboxUnreadOnly}
                onChange={(e) => setInboxUnreadOnly(e.target.checked)}
              />
              غير المقروء فقط
            </label>
            <div className="sa-inbox-actions">
              <button
                type="button"
                className="sa-btn sa-btn-primary"
                disabled={inboxBusy}
                onClick={handleInboxSync}
              >
                {inboxBusy ? 'جاري المزامنة...' : 'مزامنة من Twilio'}
              </button>
              <button
                type="button"
                className="sa-btn"
                disabled={inboxBusy || !inboxSelectedIds.length}
                onClick={() => handleInboxMarkRead(true)}
              >
                تعليم كمقروء
              </button>
              <button
                type="button"
                className="sa-btn"
                disabled={inboxBusy || !inboxSelectedIds.length}
                onClick={() => handleInboxMarkRead(false)}
              >
                تعليم كغير مقروء
              </button>
              <button
                type="button"
                className="sa-btn sa-btn-delete-confirm"
                disabled={inboxBusy || !inboxSelectedIds.length}
                onClick={handleInboxDelete}
              >
                حذف المحدد
              </button>
            </div>
          </div>

          {inboxLoading ? (
            <p className="sa-empty">جاري التحميل...</p>
          ) : (
            <div className="sa-sms-table-wrap">
              <table className="sa-sms-table sa-inbox-table">
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        checked={inboxMessages.length > 0 && inboxSelectedIds.length === inboxMessages.length}
                        onChange={toggleInboxSelectAll}
                      />
                    </th>
                    <th>الحالة</th>
                    <th>من</th>
                    <th>الصالون</th>
                    <th>الرسالة</th>
                    <th>التاريخ</th>
                  </tr>
                </thead>
                <tbody>
                  {inboxMessages.map((msg) => (
                    <tr key={msg.id} className={msg.is_read ? '' : 'sa-inbox-unread'}>
                      <td>
                        <input
                          type="checkbox"
                          checked={inboxSelectedIds.includes(msg.id)}
                          onChange={() => toggleInboxMessage(msg.id)}
                        />
                      </td>
                      <td>
                        {msg.is_read
                          ? <span className="sa-badge-muted">مقروءة</span>
                          : <span className="sa-badge-ok">جديدة</span>}
                      </td>
                      <td dir="ltr">{msg.from_phone}</td>
                      <td>{msg.account_title || '—'}</td>
                      <td className="sa-inbox-body">{msg.body || '—'}</td>
                      <td dir="ltr">
                        {msg.created_at
                          ? new Date(msg.created_at).toLocaleString('ar')
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {inboxMessages.length === 0 && (
                <p className="sa-empty">لا توجد رسائل واردة</p>
              )}
            </div>
          )}
        </>
      )}

      {activeTab === 'blacklist' && (
        <>
          <div className="sa-header">
            <h1>مستخدمون محظورون من الحجز</h1>
          </div>

          <p className="sa-blacklist-intro">
            المستخدمون الذين تجاوزوا 20 عملية حجز يومية (إنشاء، إلغاء، أو تغيير موعد).
            يمكنهم التصفح فقط حتى يتم إلغاء الحظر من هنا.
          </p>

          <div className="sa-stats">
            <div className="sa-stat-card sa-stat-danger">
              <span className="sa-stat-number">{blacklistUsers.length}</span>
              <span className="sa-stat-label">محظورون حالياً</span>
            </div>
          </div>

          {blacklistLoading ? (
            <p className="sa-empty">جاري التحميل...</p>
          ) : (
            <div className="sa-accounts-list">
              {blacklistUsers.map((user) => (
                <div key={user.id} className="sa-account-card sa-blocked">
                  <div className="sa-account-info">
                    <h3>{user.full_name || 'مستخدم'}</h3>
                    <p className="sa-account-phone" dir="ltr">{user.phone || '—'}</p>
                    <span className="sa-badge-blocked">محظور من الحجز</span>
                    {user.action_count_at_block != null && (
                      <span className="sa-badge-category">
                        {user.action_count_at_block} عملية عند الحظر
                      </span>
                    )}
                    {user.blocked_at && (
                      <p className="sa-blacklist-date">
                        تاريخ الحظر: {new Date(user.blocked_at).toLocaleString('ar-EG')}
                      </p>
                    )}
                  </div>
                  <div className="sa-account-actions">
                    <button
                      className="sa-btn sa-btn-unblock"
                      onClick={() => handleUnblockBookingUser(user.user_id)}
                      disabled={unblockingUserId === user.user_id}
                    >
                      {unblockingUserId === user.user_id ? '...' : '✓ إلغاء الحظر'}
                    </button>
                  </div>
                </div>
              ))}
              {blacklistUsers.length === 0 && (
                <p className="sa-empty">لا يوجد مستخدمون محظورون حالياً</p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
